import React, { useEffect, useState } from 'react'
import { Plus, ArrowUp, ArrowDown, Trash2, ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Badge } from '../ui/badge'
import { Textarea } from '../ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { SECCION_TIPOS, getSeccionTipoLabel, getSeccionTipoColor } from '../../constants/seccionTipos'
import { getDeporteHabilidades } from '../../lib/supabase'
import HabilidadesSelector from './HabilidadesSelector'
import { resolverHabilidadesSeccion } from './programaEditorHelpers'

const SECCION_DEFAULT = {
  nombre: '',
  duracion_seg: 300,
  descripcion: '',
  tipo: 'otro',
  habilidad_ids: []
}

const formatDuracion = (totalSeg) => {
  if (!totalSeg) return '0 min'
  const horas = Math.floor(totalSeg / 3600)
  const minutos = Math.floor((totalSeg % 3600) / 60)
  if (horas > 0) {
    return minutos > 0 ? `${horas}h ${minutos} min` : `${horas}h`
  }
  return `${minutos} min`
}

const formatDuracionInput = (totalSeg) => {
  const min = Math.floor(totalSeg / 60)
  const seg = totalSeg % 60
  return { min, seg }
}

const SeccionesBuilder = ({ secciones = [], onChange, deportes = [] }) => {
  const [expandedIdx, setExpandedIdx] = useState(null)
  const [catalogo, setCatalogo] = useState([])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (!deportes.length) { setCatalogo([]); return }
      const todas = []
      const vistos = new Set()
      for (const deporte of deportes) {
        const { data } = await getDeporteHabilidades(deporte)
        for (const hab of data || []) {
          if (!vistos.has(hab.id)) { vistos.add(hab.id); todas.push(hab) }
        }
      }
      if (!cancelled) setCatalogo(todas)
    }
    load()
    return () => { cancelled = true }
  }, [deportes.join('|')]) // eslint-disable-line react-hooks/exhaustive-deps

  const totalSeg = secciones.reduce((sum, s) => sum + (s.duracion_seg || 0), 0)

  const updateSeccion = (idx, patch) => {
    const next = secciones.map((s, i) => (i === idx ? { ...s, ...patch } : s))
    onChange(next)
  }

  const addSeccion = () => {
    const next = [...secciones, { ...SECCION_DEFAULT }]
    onChange(next)
    setExpandedIdx(next.length - 1)
  }

  const removeSeccion = (idx) => {
    onChange(secciones.filter((_, i) => i !== idx))
    if (expandedIdx === idx) setExpandedIdx(null)
  }

  const moveUp = (idx) => {
    if (idx === 0) return
    const next = [...secciones]
    ;[next[idx - 1], next[idx]] = [next[idx], next[idx - 1]]
    onChange(next)
  }

  const moveDown = (idx) => {
    if (idx === secciones.length - 1) return
    const next = [...secciones]
    ;[next[idx + 1], next[idx]] = [next[idx], next[idx + 1]]
    onChange(next)
  }

  const handleDuracionChange = (idx, field, value) => {
    const num = Math.max(0, parseInt(value, 10) || 0)
    const current = formatDuracionInput(secciones[idx].duracion_seg)
    const nuevoTotal =
      field === 'min'
        ? num * 60 + current.seg
        : current.min * 60 + Math.min(num, 59)
    updateSeccion(idx, { duracion_seg: nuevoTotal })
  }

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {secciones.length === 0 ? (
          <div className="rounded-md border border-dashed border-input px-4 py-8 text-center text-sm text-muted-foreground">
            Aun no hay secciones. Agrega la primera abajo.
          </div>
        ) : (
          secciones.map((seccion, idx) => {
            const isExpanded = expandedIdx === idx
            const tipoColor = getSeccionTipoColor(seccion.tipo)
            const dur = formatDuracionInput(seccion.duracion_seg)
            return (
              <div key={idx} className="rounded-lg border border-border bg-card">
                <div className="flex items-center gap-2 px-3 py-2">
                  <div className="flex flex-col">
                    <button
                      type="button"
                      onClick={() => moveUp(idx)}
                      disabled={idx === 0}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                      aria-label="Subir seccion"
                    >
                      <ArrowUp className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveDown(idx)}
                      disabled={idx === secciones.length - 1}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                      aria-label="Bajar seccion"
                    >
                      <ArrowDown className="h-3 w-3" />
                    </button>
                  </div>
                  <span className="text-xs font-semibold text-muted-foreground w-6">{idx + 1}</span>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground truncate">
                      {seccion.nombre || <span className="italic text-muted-foreground">Sin nombre</span>}
                    </p>
                    <Badge variant="outline" className={`text-[10px] mt-0.5 ${tipoColor}`}>
                      {`• ${getSeccionTipoLabel(seccion.tipo)}`}
                    </Badge>
                    {resolverHabilidadesSeccion(seccion.habilidad_ids, catalogo).length > 0 && (
                      <span className="mt-1 flex flex-wrap gap-1">
                        {resolverHabilidadesSeccion(seccion.habilidad_ids, catalogo).map((h) => (
                          <Badge key={h.id} variant="outline"
                            className="text-[10px] border-blue-200 text-blue-700">
                            {h.nombre}
                          </Badge>
                        ))}
                      </span>
                    )}
                  </div>
                  <span className="text-sm font-mono text-foreground w-16 text-right">
                    {`${dur.min}:${dur.seg.toString().padStart(2, '0')}`}
                  </span>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => setExpandedIdx(isExpanded ? null : idx)}
                    aria-label={isExpanded ? 'Contraer' : 'Expandir'}
                  >
                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => removeSeccion(idx)}
                    aria-label="Eliminar seccion"
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
                {isExpanded && (
                  <div className="border-t border-border px-3 py-3 space-y-3 bg-muted/50">
                    <div>
                      <Label>Nombre</Label>
                      <Input
                        value={seccion.nombre}
                        onChange={(e) => updateSeccion(idx, { nombre: e.target.value })}
                        placeholder="Ej: Flex Dinamico, Rutas y persecucion"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-2 items-end">
                      <div>
                        <Label>Minutos</Label>
                        <Input
                          type="number"
                          min="0"
                          value={dur.min}
                          onChange={(e) => handleDuracionChange(idx, 'min', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label>Segundos</Label>
                        <Input
                          type="number"
                          min="0"
                          max="59"
                          value={dur.seg}
                          onChange={(e) => handleDuracionChange(idx, 'seg', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label>Tipo</Label>
                        <Select
                          value={seccion.tipo}
                          onValueChange={(v) => updateSeccion(idx, { tipo: v })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {SECCION_TIPOS.map((t) => (
                              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label>Descripción (opcional)</Label>
                      <Textarea
                        value={seccion.descripcion || ''}
                        onChange={(e) => updateSeccion(idx, { descripcion: e.target.value })}
                        rows={2}
                        placeholder="Detalles del entrenamiento para esta seccion"
                      />
                    </div>
                    <HabilidadesSelector
                      deportes={deportes}
                      selectedIds={seccion.habilidad_ids || []}
                      onChange={(ids) => updateSeccion(idx, { habilidad_ids: ids })}
                    />
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>

      <Button variant="outline" onClick={addSeccion}>
        <Plus className="h-4 w-4 mr-1" />
        Agregar seccion
      </Button>

      <div className="text-sm text-muted-foreground text-right">
        Total: <strong>{formatDuracion(totalSeg)}</strong> · {secciones.length} secciones
      </div>
    </div>
  )
}

export default SeccionesBuilder
